import React, { useEffect, useState } from "react";
import { Phone, X, Send, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { WhatsAppIcon, buildWhatsAppLink } from "./WhatsAppIcon";

const REASONS = [
  { v: "order_help", l: "Help with an order" },
  { v: "delivery_issue", l: "Delivery issue" },
  { v: "wholesale", l: "Wholesale enquiry" },
  { v: "subscription", l: "Subscription question" },
  { v: "missing_invoice", l: "Missing invoice / receipt" },
  { v: "complaint", l: "Complaint" },
  { v: "general", l: "General enquiry" },
];

export default function CallbackWidget() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState(null);
  const loc = useLocation();
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    reason: "general", message: "", preferred_time: "ASAP",
  });

  useEffect(() => {
    api.get("/cms").then(({ data }) => setContact(data?.contact || null)).catch(() => {});
  }, []);

  const waHref = buildWhatsAppLink(contact?.whatsapp_number, contact?.whatsapp_message);

  // Hide on admin/driver/wholesale dashboards
  const path = loc.pathname;
  if (path.startsWith("/admin") || path.startsWith("/driver/dashboard") || path.startsWith("/driver/delivery") || path.startsWith("/wholesale/dashboard") || path.startsWith("/wholesale/products") || path.startsWith("/wholesale/orders")) {
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Strip empty optional fields so Pydantic's EmailStr doesn't reject ""
      const payload = { ...form, page_path: path };
      if (!payload.email) delete payload.email;
      if (!payload.message) delete payload.message;
      await api.post("/callbacks", payload);
      setSent(true);
      toast.success("We'll call you back shortly 📞");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || "Invalid field").join(", ")
        : (typeof detail === "string" ? detail : "Couldn't send request");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const reset = () => {
    setSent(false);
    setOpen(false);
    setForm({ name: "", phone: "", email: "", reason: "general", message: "", preferred_time: "ASAP" });
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="callback-fab"
        className="fixed bottom-24 sm:bottom-24 right-4 sm:right-6 z-40 group bg-[#EA580C] hover:bg-[#c2410c] text-white rounded-full shadow-2xl px-4 sm:px-5 py-3.5 sm:py-4 flex items-center gap-2 font-bold transition-all hover:-translate-y-0.5"
        aria-label="Request a callback"
      >
        <Phone size={18} className="group-hover:rotate-12 transition-transform" />
        <span className="hidden sm:inline">Need help?</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end bg-black/40 sm:bg-transparent sm:p-6" onClick={reset} data-testid="callback-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:w-[420px] max-h-[92vh] overflow-y-auto">
            {sent ? (
              <div className="p-8 text-center">
                <div className="h-16 w-16 rounded-full bg-green-100 text-[#166534] mx-auto flex items-center justify-center text-3xl">📞</div>
                <h3 className="font-display font-black text-2xl text-stone-900 mt-5">We've got your number.</h3>
                <p className="text-stone-600 mt-2 text-sm">A team member will call <b>{form.phone}</b> within the hour during business hours. You'll get an email confirmation too.</p>
                <button onClick={reset} data-testid="callback-close" className="mt-7 w-full bg-[#0E2818] hover:bg-black text-white rounded-full font-bold py-3">Done</button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="bg-gradient-to-br from-[#0E2818] to-[#166534] text-white p-6 flex items-start justify-between">
                  <div>
                    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#F4C430] text-[#0E2818] rounded-full px-2.5 py-0.5">
                      <Phone size={10} /> Call me back
                    </div>
                    <div className="font-display font-black text-2xl mt-3 leading-tight">We'll ring you within an hour.</div>
                    <div className="text-stone-300 text-xs mt-1.5">Mon–Sat · 7 am – 6 pm NZST</div>
                  </div>
                  <button type="button" onClick={reset} data-testid="callback-close-btn" className="text-white/70 hover:text-white p-1 -mr-1 -mt-1">
                    <X size={20} />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {waHref && (
                    <a
                      href={waHref}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="callback-whatsapp-btn"
                      className="flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] hover:bg-[#1ebd5b] text-white font-bold py-3.5 transition-colors"
                    >
                      <WhatsAppIcon size={18} className="text-white" /> Chat on WhatsApp
                    </a>
                  )}
                  {waHref && (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-stone-200" />
                      <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Or request a callback</span>
                      <div className="flex-1 h-px bg-stone-200" />
                    </div>
                  )}

                  <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cb-name" required />
                  <Field label="NZ phone number" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="cb-phone" required placeholder="022 373 4083" />
                  <Field label="Email (optional)" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="cb-email" />

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">What's it about?</label>
                    <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cb-reason"
                      className="mt-1.5 w-full rounded-xl border border-stone-200 bg-white px-3 py-3 outline-none focus:border-[#166534] text-sm">
                      {REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Message (optional)</label>
                    <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="cb-message" rows={3} maxLength={500}
                      placeholder="Tell us anything that helps us call you back faster…"
                      className="mt-1.5 w-full rounded-xl border border-stone-200 bg-white px-3 py-3 outline-none focus:border-[#166534] text-sm resize-none" />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Best time to call</label>
                    <div className="mt-1.5 grid grid-cols-3 gap-2">
                      {["ASAP", "Morning", "Afternoon"].map((t) => (
                        <button key={t} type="button" onClick={() => setForm({ ...form, preferred_time: t })} data-testid={`cb-time-${t.toLowerCase()}`}
                          className={`rounded-xl border-2 py-2 text-xs font-bold transition-colors ${form.preferred_time === t ? "border-[#166534] bg-green-50 text-[#166534]" : "border-stone-200 text-stone-600 hover:border-stone-400"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button type="submit" disabled={busy} data-testid="cb-submit" className="w-full bg-[#EA580C] hover:bg-[#c2410c] disabled:opacity-50 text-white rounded-full font-bold py-3.5 flex items-center justify-center gap-2 mt-3">
                    {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={14} /> Request callback</>}
                  </button>

                  <div className="text-center text-[11px] text-stone-500 mt-1">
                    Or call us directly: <a href="tel:0223734083" className="font-bold text-[#166534]">022 373 4083</a>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, placeholder }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{label}</label>
      <input type={type} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-stone-200 bg-white px-3 py-3 outline-none focus:border-[#166534] text-sm" />
    </div>
  );
}
